import { Box, Divider, Text } from '@wix/design-system';
import React, { useContext, useReducer, useState } from 'react';
import { RicosContext } from 'ricos-context';
import { SliderWithInput, ToggledColorPicker } from 'wix-rich-content-ui-components';
import { s as styleInject } from '../style-inject.es-e7b07dd7.js';
import { BORDER_WIDTH_SLIDER, BORDER_WIDTH_INPUT, CORNER_RADIUS_SLIDER, CORNER_RADIUS_INPUT, TEXT_COLOR_TOGGLE, BORDER_COLOR_TOGGLE, BACKGROUND_COLOR_TOGGLE } from '../dataHooks.js';
import { resolveColors } from '../utils/resolve-colors.js';
import 'wix-rich-content-common';
import '../defaults.js';

var css_248z = ".GxMuL{--wds-list-item-section-padding-horizontal:0px}.pqd91{width:fit-content}[dir] .pqd91{box-shadow:0 0 18px rgba(0,6,36,.1),0 6px 6px rgba(0,6,36,.05)}";
var classes = {"container":"GxMuL","colorPicker":"pqd91"};
styleInject(css_248z);

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
const DesignComponent = ({
  stylesData,
  config,
  onChange
}) => {
  const { t } = useContext(RicosContext);
  const [openedPicker, togglePicker] = useReducer(
    (prevState, picker) => {
      if (prevState === picker) {
        return null;
      }
      return picker;
    },
    null
  );
  const [userColors, setUserColors] = useState(() => ({
    textColors: resolveColors({
      colors: config.colors,
      currentColor: stylesData.colors.text,
      getColorFn: config.getTextColors
    }),
    borderColors: resolveColors({
      colors: config.colors,
      currentColor: stylesData.colors.border,
      getColorFn: config.getBorderColors
    }),
    backgroundColors: resolveColors({
      colors: config.colors,
      currentColor: stylesData.colors.background,
      getColorFn: config.getBackgroundColors
    })
  }));
  const handleOnChange = (data) => {
    onChange({
      border: __spreadValues(__spreadValues({}, stylesData.border), data.border),
      colors: __spreadValues(__spreadValues({}, stylesData.colors), data.colors)
    });
  };
  return /* @__PURE__ */ React.createElement(Box, { direction: "vertical", gap: "SP4", className: classes.container }, /* @__PURE__ */ React.createElement(Section, { title: t("ButtonModal_Border_Section") }, /* @__PURE__ */ React.createElement(
    SliderWithInput,
    {
      min: 0,
      max: 15,
      onChange: (width) => handleOnChange({ border: { width } }),
      label: t("ButtonModal_Width_Input"),
      sliderDataHook: BORDER_WIDTH_SLIDER,
      inputDataHook: BORDER_WIDTH_INPUT,
      value: stylesData.border.width
    }
  ), /* @__PURE__ */ React.createElement(
    SliderWithInput,
    {
      min: 0,
      max: 30,
      onChange: (radius) => handleOnChange({ border: { radius } }),
      label: t("ButtonModal_Radius_Input"),
      sliderDataHook: CORNER_RADIUS_SLIDER,
      inputDataHook: CORNER_RADIUS_INPUT,
      value: stylesData.border.radius
    }
  )), /* @__PURE__ */ React.createElement(Divider, null), /* @__PURE__ */ React.createElement(Section, { title: t("ButtonModal_Color_Section") }, /* @__PURE__ */ React.createElement(Box, { direction: "vertical" }, /* @__PURE__ */ React.createElement(
    ToggledColorPicker,
    {
      label: t("ButtonModal_Text_Color"),
      color: stylesData.colors.text.toUpperCase(),
      userColors: userColors.textColors,
      onNewColorAdded: (color) => {
        var _a;
        (_a = config.onTextColorAdded) == null ? void 0 : _a.call(config, color);
        setUserColors((prev) => __spreadProps(__spreadValues({}, prev), { textColors: [...prev.textColors, color] }));
        handleOnChange({ colors: { text: color } });
      },
      onChange: (text) => handleOnChange({ colors: { text } }),
      dataHook: TEXT_COLOR_TOGGLE,
      onToggle: () => togglePicker("textColor"),
      isExpanded: openedPicker === "textColor",
      colorPickerClassName: classes.colorPicker
    }
  ), openedPicker === "textColor" && /* @__PURE__ */ React.createElement(Divider, null)), /* @__PURE__ */ React.createElement(Box, { direction: "vertical" }, /* @__PURE__ */ React.createElement(
    ToggledColorPicker,
    {
      label: t("ButtonModal_Border_Color"),
      color: stylesData.colors.border.toUpperCase(),
      userColors: userColors.borderColors,
      onNewColorAdded: (color) => {
        var _a;
        (_a = config.onBorderColorAdded) == null ? void 0 : _a.call(config, color);
        setUserColors((prev) => __spreadProps(__spreadValues({}, prev), { borderColors: [...prev.borderColors, color] }));
        handleOnChange({ colors: { border: color } });
      },
      onChange: (border) => handleOnChange({ colors: { border } }),
      dataHook: BORDER_COLOR_TOGGLE,
      onToggle: () => togglePicker("borderColor"),
      isExpanded: openedPicker === "borderColor",
      colorPickerClassName: classes.colorPicker
    }
  ), openedPicker === "borderColor" && /* @__PURE__ */ React.createElement(Divider, null)), /* @__PURE__ */ React.createElement(Box, { direction: "vertical" }, /* @__PURE__ */ React.createElement(
    ToggledColorPicker,
    {
      label: t("ButtonModal_Background_Color"),
      color: stylesData.colors.background.toUpperCase(),
      userColors: userColors.backgroundColors,
      onNewColorAdded: (color) => {
        var _a;
        (_a = config.onBackgroundColorAdded) == null ? void 0 : _a.call(config, color);
        setUserColors((prev) => __spreadProps(__spreadValues({}, prev), {
          backgroundColors: [...prev.backgroundColors, color]
        }));
        handleOnChange({ colors: { background: color } });
      },
      onChange: (background) => handleOnChange({ colors: { background } }),
      dataHook: BACKGROUND_COLOR_TOGGLE,
      onToggle: () => togglePicker("backgroundColor"),
      isExpanded: openedPicker === "backgroundColor",
      colorPickerClassName: classes.colorPicker
    }
  ), openedPicker === "backgroundColor" && /* @__PURE__ */ React.createElement(Divider, null))));
};
const Section = ({ title, children }) => {
  return /* @__PURE__ */ React.createElement(Box, { direction: "vertical", gap: "SP2" }, /* @__PURE__ */ React.createElement(Box, { paddingBottom: "SP1" }, /* @__PURE__ */ React.createElement(Text, { size: "small", weight: "bold" }, title)), children);
};

export { DesignComponent };
//# sourceMappingURL=design-component.js.map
